import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/styles';
import React, { FunctionComponent, useState } from 'react';
import { useAuthUser } from '../../../../../contexts/AuthProvider/hooks';
import {
  hasOneOfRoles,
  Role,
} from '../../../../../contexts/AuthProvider/types';
import ButtonProgress from '../../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../../eliot-components/src/ConfigProvider/hooks';
import { useCreateOrgMutation } from '../../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../../eliot-components/src/utils/helpers';
import { Chip, Grid, InputAdornment } from '@material-ui/core';
import { Add } from '@material-ui/icons';

const useStyles = makeStyles((theme: Theme) => ({
  fab: {
    position: 'fixed',
    right: theme.spacing(4),
    bottom: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      right: theme.spacing(2),
      bottom: theme.spacing(2),
    },
  },
  dialogContent: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  dialog: {
    '& .MuiDialog-paperFullWidth': {
      minWidth: 0,
    },
    '& .MuiFormGroup-root': {
      marginBottom: 5,
    },
  },
  orgName: {
    '& .MuiFormHelperText-root': {
      color: theme.palette.primary.main,
    },
  },
}));

interface Props {
  onCreated?(name: string): void;
  onClose(): void;
  isOpen: boolean;
  hasGlobalOrgAdminRole: boolean;
  email: string;
}

const CreateOrgDialog: FunctionComponent<Props> = ({
  onCreated,
  onClose,
  isOpen,
  hasGlobalOrgAdminRole,
  email,
}) => {
  const classes = useStyles();
  const { authUser } = useAuthUser();
  const { apiBaseURL } = useConfig();
  const [createOrg, { isLoading }] = useCreateOrgMutation();
  const [name, setName] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const restricted = true;
  const [maxUsers, setMaxUsers] = useState(0);
  const [maxWebhooks, setMaxWebhooks] = useState(1);
  const emailDomain = email.split('@')[1]?.toLowerCase();
  const defaultDomain =
    !hasGlobalOrgAdminRole && emailDomain ? [emailDomain] : [];
  const [domain, setDomain] = useState<string[]>(defaultDomain);
  const [domainInput, setDomainInput] = useState('');
  const [domainError, setDomainError] = useState('');
  if (!hasOneOfRoles(authUser, [Role.OrganizationManager])) {
    return null;
  }

  const addDomain = () => {
    const regex = new RegExp(
      `^(?:[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*\\.)+[a-zA-Z]{2,}$`
    );
    const trimmedInput = domainInput.toLowerCase().trim();

    if (
      trimmedInput &&
      regex.exec(trimmedInput) &&
      !domain.includes(trimmedInput)
    ) {
      setDomain([...domain, trimmedInput]);
      setDomainInput('');
      setDomainError('');
    } else {
      // show validation error
      if (!trimmedInput) {
        setDomainError('Domain cannot be empty');
      } else if (domain.includes(trimmedInput)) {
        setDomainError('Domain already exists');
      } else if (!regex.exec(trimmedInput)) {
        setDomainError('Invalid domain format');
      }
    }
  };

  const removeDomain = (domainToRemove: string) => {
    setDomain(domain.filter((d) => d !== domainToRemove));
  };

  const handleClose = () => {
    setName('');
    setMaxUsers(0);
    setMaxWebhooks(1);
    setDomain(defaultDomain);
    setDomainInput('');
    setDomainError('');
    onClose();
  };

  return (
    <>
      <Dialog
        className={classes.dialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="dialog-title"
        open={isOpen}
        onClose={() => {
          handleClose();
        }}
      >
        <DialogTitle id="dialog-title">Create Org</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            margin="dense"
            variant="outlined"
            id="name"
            label="Name"
            inputProps={{ maxLength: 60 }}
            className={classes.orgName}
            autoFocus
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            helperText={
              name && name.length === 60
                ? 'Maximum character length reached'
                : null
            }
          />
          <br />
          <br />
          <Grid container spacing={2}>
            <Grid item sm={6} xs={12}>
              <TextField
                margin="dense"
                variant="outlined"
                id="max_users"
                label="Max Users"
                className={classes.orgName}
                value={maxUsers}
                onChange={(e) => {
                  let val = parseInt(e.target.value);
                  if (!val) {
                    val = 0;
                  }
                  setMaxUsers(val);
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                margin="dense"
                variant="outlined"
                id="max_webhooks"
                label="Max Webhooks"
                className={classes.orgName}
                value={maxWebhooks}
                onChange={(e) => {
                  let val = parseInt(e.target.value);
                  if (!val) {
                    val = 0;
                  }
                  setMaxWebhooks(val);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                variant="outlined"
                id="domain"
                label="Domain"
                placeholder="Enter domain (e.g., example.com)"
                className={classes.orgName}
                value={domainInput}
                onChange={(e) => {
                  setDomainInput(e.target.value);
                  if (domainError) setDomainError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addDomain();
                  }
                }}
                error={!!domainError}
                helperText={domainError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Add
                        color="primary"
                        onClick={addDomain}
                        style={{
                          cursor: 'pointer',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {domain.length > 0 && (
                <Grid container spacing={1} style={{ marginTop: 8 }}>
                  {domain.map((d, index) => (
                    <Grid item key={index}>
                      <Chip
                        label={d}
                        onDelete={() => removeDomain(d)}
                        color="primary"
                        variant="outlined"
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleClose();
            }}
          >
            Cancel
          </Button>
          <ButtonProgress
            variant="contained"
            color="primary"
            isLoading={isLoading}
            disabled={name.length === 0}
            onClick={() => {
              let orgObj = {
                apiBaseURL,
                name,
              };
              // @ts-ignore
              orgObj.restricted = restricted;
              // @ts-ignore
              orgObj.restrictedOpts = {
                maxUsers: maxUsers,
                maxWebhooks: maxWebhooks,
                domain: domain,
              };
              createOrg(orgObj).then((resp: any) => {
                if (!resp.error) {
                  if (onCreated) {
                    enqueueSnackbar('Organization created', {
                      variant: 'success',
                    });
                    onCreated(name);
                    return;
                  }
                  handleClose();
                }

                enqueueSnackbar(parseErrorMessage(resp.error), {
                  variant: 'error',
                });
              });
            }}
          >
            Submit
          </ButtonProgress>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateOrgDialog;
